import React from 'react'

import {
  AlignItems,
  Body3,
  Box,
  Color,
  Display,
  DisplayPosition,
  FlexDirection,
  FontFamily,
  FontWeight,
  Icon,
  IconVariant,
  Text,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import type {
  DeliveryDayDiscount,
  ResubscriptionOfferData,
} from '@library/api-customer-engagement-experience'

import heroImage from '../assets/resubscribe-hero-box.webp'

const OfferCardSurface = withCitrusTheme(Box, () => ({
  borderRadius: '8px',
  overflow: 'hidden',
}))

const OfferBadge = withCitrusTheme(Box, () => ({
  borderRadius: '4px',
}))

// One source image, cropped to each breakpoint's aspect ratio. `fill` makes it take the offer
// card's remaining height on desktop, where the card stretches to match the settings column.
const HeroImage = ({ aspectRatio, fill }: { aspectRatio: string; fill?: boolean }) => (
  <Box
    width="100%"
    height={fill ? '100%' : undefined}
    style={{
      aspectRatio: fill ? undefined : aspectRatio,
      backgroundImage: `url(${heroImage})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
    }}
  />
)

const OfferHero = () => (
  <>
    <Box display={[Display.Block, Display.Block, Display.None, Display.None]}>
      <HeroImage aspectRatio="342 / 77" />
    </Box>
    <Box display={[Display.None, Display.None, Display.Block, Display.None]}>
      <HeroImage aspectRatio="736 / 166" />
    </Box>
    <Box display={[Display.None, Display.None, Display.None, Display.Block]} flexGrow={1}>
      <HeroImage aspectRatio="583 / 148" fill />
    </Box>
  </>
)

type OfferCardProps = {
  offer: ResubscriptionOfferData
  nextBoxDateLabel: string
  discount?: DeliveryDayDiscount
  benefits: string[]
}

export const OfferCard = ({ offer, nextBoxDateLabel, discount, benefits }: OfferCardProps) => (
  <OfferCardSurface
    position={DisplayPosition.Relative}
    display={Display.Flex}
    flexDirection={FlexDirection.Column}
    bg={Color.ColdGrey_800}
    width={['100%', '100%', '100%', '50%']}
  >
    <OfferHero />

    {discount && (
      <Box position={DisplayPosition.Absolute} top="12px" right="12px">
        <OfferBadge bg={Color.Success_50} paddingH={2} paddingV={0.5}>
          <Text
            size={4}
            fontFamily={FontFamily.Bold}
            fontWeight={FontWeight.Bold}
            color={Color.Success_900}
          >
            {discount.badge}
          </Text>
        </OfferBadge>
      </Box>
    )}

    <Box
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      gap={4}
      paddingV={3}
      paddingH={4}
    >
      <Box display={Display.Flex} flexDirection={FlexDirection.Column} gap={1}>
        <Body3 color={Color.White}>{offer.next_box_intro}</Body3>
        <Text size={9} fontFamily={FontFamily.Bold} color={Color.White}>
          {nextBoxDateLabel}
        </Text>
      </Box>
      <Box display={Display.Flex} flexDirection={FlexDirection.Column} gap={1}>
        {benefits.map((benefit) => (
          <Box
            key={benefit}
            display={Display.Flex}
            alignItems={AlignItems.Center}
            gap={1}
            color={Color.White}
          >
            <Icon name="tick" size={4} variant={IconVariant.Inherit} />
            <Body3 color={Color.White}>{benefit}</Body3>
          </Box>
        ))}
      </Box>
    </Box>
  </OfferCardSurface>
)
